<template>
  <header :class="{ 'menu-open': showMenu }">

    <!-- Unified header graphic: topbar + swash bracket as one connected SVG -->
    <div class="graphic-wrapper">
      <HeaderGraphic />
    </div>

    <div class="header-bar">
      <router-link class="logo" :to="`/${lang}`" @click="showMenu = 0">
        Quo Vadis
      </router-link>

      <!-- Social icons -->
      <div class="social" v-if="kql.site?.facebook || kql.site?.instagram">
        <a v-if="kql.site.facebook" :href="kql.site.facebook" class="social-icon" target="_blank" rel="noopener noreferrer">FB</a>
        <a v-if="kql.site.instagram" :href="kql.site.instagram" class="social-icon" target="_blank" rel="noopener noreferrer">IG</a>
      </div>

      <!-- Desktop navigation -->
      <nav class="desktop-nav" v-if="navPairs.length">
        <div class="nav-col" v-for="(pair, i) in navPairs" :key="i">
          <router-link
            v-for="page in pair"
            :key="page.id"
            :to="pathName(page.url)"
            v-html="page.title"
            @click="showMenu = 0"
          />
        </div>
      </nav>

      <!-- Mobile hamburger -->
      <MenuToggle class="mobile-toggle" :open="showMenu" @click="showMenu = showMenu ? 0 : 1" />
    </div>

    <MenuNavigation />
  </header>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { yamlToArray, pathName, pageFromPath } from '@/utils'
import { composables } from '@/composables'

const kql = useStore()
const { showMenu } = composables()
const lang = document.documentElement.lang || 'fi'

const navPages = computed(() => {
  const uids = yamlToArray(kql.site?.navigation)
  return uids.map(uid => pageFromPath(uid, kql.pages)).filter(Boolean)
})

const navPairs = computed(() => {
  const pages = navPages.value
  const pairs = []
  for (let i = 0; i < pages.length; i += 2) {
    pairs.push(pages.slice(i, i + 2))
  }
  return pairs
})
</script>

<style scoped>
header {
  --fill-0: var(--fg);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  overflow: visible;
}

/* ── Header graphic (topbar + swash as one SVG) ── */
.graphic-wrapper {
  position: absolute;
  top: 0;
  left: var(--offset);
  right: 0;
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

/* ── Main bar ── */
.header-bar {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem var(--offset) 0;
  position: relative;
  z-index: 1;
}

/* ── Logo ── */
.logo {
  font-family: var(--serif);
  font-size: 1.5rem;
  letter-spacing: -0.01em;
  flex-shrink: 0;
  position: relative;
  z-index: 201;
  line-height: 1;
  padding-top: 0.1em;
}

/* ── Social icons ── */
.social {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  padding-top: 0.2rem;
  flex-shrink: 0;
}

.social-icon {
  width: 2.6rem;
  height: 2.6rem;
  border: 1.5px solid currentColor;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--sans);
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  transition: opacity 150ms;
}

.social-icon:hover {
  opacity: 0.4;
}

/* ── Desktop nav ── */
.desktop-nav {
  display: flex;
  gap: clamp(1rem, 2vw, 2rem);
  margin-left: auto;
}

.nav-col {
  display: flex;
  flex-direction: column;
}

.nav-col a {
  font-family: var(--sans);
  font-weight: 700;
  font-stretch: condensed;
  font-size: clamp(28px, 3.2vw, 52px);
  line-height: 0.9;
  letter-spacing: -0.03em;
  display: block;
  transition: opacity 150ms;
}

.nav-col a:hover {
  opacity: 0.35;
}

.nav-col a.router-link-active {
  opacity: 0.35;
}

/* ── Mobile toggle (hidden on desktop) ── */
.mobile-toggle {
  display: none;
}

/* ── Mobile ── */
@media (--tablet) {
  .graphic-wrapper,
  .social,
  .desktop-nav {
    display: none;
  }

  .mobile-toggle {
    display: flex;
  }

  .header-bar {
    align-items: center;
    justify-content: space-between;
    padding-top: 2rem;
  }
}
</style>
